import { Alert, Button } from "antd";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams as useRouteParams } from "react-router";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Grid as ZhGrid,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";

import request from "@/api";
import { DATE_PICKER_FORMAT, DATETIME_PICKER_FORMAT, DEFAULT_VALUE } from "@/config";
import type { Delivery, File as DeliveryFile, GoodsShip } from "@/entity/purchasing/delivery";
import { PAYMENT_TYPE } from "@/meta/purchase/payment";
import { SIGNATURE_STATUS } from "@/meta/purchase/delivery";
import { dateFormat, dateTimeFormat, getRealyState } from "@/utils";
import { getShipName } from "@/utils/ship";
import { formatNumber } from "@/utils/number";
import { GOODS_STATE } from "@zhihaoscm/meta";

type DeliveryShip = Partial<GoodsShip> & { captinMobile?: string };

export interface DeliveryDetailData {
    purchaseGoods?: Partial<Delivery> & { ships?: DeliveryShip[] };
    file?: Partial<DeliveryFile>;
}

export interface DeliveryDetailPageProps {
    deliveryId?: number | string;
    loadDetail?: (id: number | string) => Promise<DeliveryDetailData>;
    loadFile?: (id: number | string) => Promise<Partial<DeliveryFile>>;
}

/** 直接复用 Vue deliveryService.getDeliveryVo 的详情接口，避免 React 页面触发 decorators。 */
export async function loadDeliveryDetail(id: number | string): Promise<DeliveryDetailData> {
    const { data } = await request.service.get<DeliveryDetailData>(
        `/admin/delivery/vo/${encodeURIComponent(String(id))}`
    );
    return data;
}

/** 复用 Vue BaseItem 的 fileId 契约，查询作废文件的名称和可访问地址。 */
export async function loadDeliveryFile(id: number | string): Promise<Partial<DeliveryFile>> {
    const { data } = await request.service.get<Partial<DeliveryFile>>(
        `/dataService/admin/file/${encodeURIComponent(String(id))}`
    );
    return data;
}

/** 详情字段缺失时沿用 Vue BaseItem 的默认占位符。 */
function display(value: unknown): string {
    return value === undefined || value === null || value === "" ? DEFAULT_VALUE : String(value);
}

/** 详情企业对象字段可选时沿用 Vue 展示名称回退顺序。 */
function enterpriseLabel(value?: { name?: string; realName?: string; mobile?: string }): string {
    return value?.name || value?.realName || value?.mobile || DEFAULT_VALUE;
}

/** 兼容接口返回的高位状态和测试/旧数据中的低位状态。 */
function stateCode(value?: number | string): number | undefined {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return undefined;
    return Math.abs(numericValue) >= 1 << 16 ? getRealyState(numericValue) : numericValue;
}

/** 将 Vue 页面状态码转换为状态文案。 */
function stateLabel(value?: number | string): string {
    return GOODS_STATE.getIntroByCode(stateCode(value)) || DEFAULT_VALUE;
}

/** 采购提货详情：保留 Vue 字段、关联跳转、返回和失败反馈。 */
export function DeliveryDetailPage({
    deliveryId,
    loadDetail = loadDeliveryDetail,
    loadFile = loadDeliveryFile
}: DeliveryDetailPageProps) {
    const params = useRouteParams<{ id?: string }>();
    const navigate = useNavigate();
    const currentId = deliveryId ?? params.id ?? "";
    const [detail, setDetail] = useState<DeliveryDetailData>();
    const [invalidFile, setInvalidFile] = useState<Partial<DeliveryFile>>();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        if (!currentId) return;
        let disposed = false;
        void loadDetail(currentId)
            .then(value => {
                if (disposed) return;
                setDetail(value);
                setInvalidFile(undefined);
                setErrorMessage(null);
                const invalidFileId = value.purchaseGoods?.invalidFileId;
                if (invalidFileId) {
                    // Vue BaseItem 在传入 fileId 后异步读取文件资源，React 侧保持同一数据来源。
                    void loadFile(invalidFileId)
                        .then(file => {
                            if (!disposed) setInvalidFile(file);
                        })
                        .catch(() => {
                            // 文件服务失败时沿用 Vue BaseItem 的静默回退，不阻断详情页其他字段展示。
                        });
                }
            })
            .catch(() => {
                if (disposed) return;
                setDetail(undefined);
                setErrorMessage("提货详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [currentId, loadDetail, loadFile]);

    const purchaseGoods = detail?.purchaseGoods;
    const goodsState = stateCode(purchaseGoods?.state);
    const invalid = goodsState === GOODS_STATE.INVALIDING.code || goodsState === GOODS_STATE.INVALID.code;
    const fullyInvalid = goodsState === GOODS_STATE.INVALID.code;
    const ships = purchaseGoods?.ships || [];

    // 用 zhihao-ui-react 列契约承接 Vue 关联船运单的船舶、吨数、跳转和备注字段。
    const shipColumns: TableColumnProps<DeliveryShip>[] = [
        { key: "shipName", label: "船舶", minWidth: 176, prop: "shipName", render: ({ row }) => getShipName(row) },
        { key: "ton", label: "运货吨数(吨)", width: 300, prop: "ton", render: ({ row }) => formatNumber(row.ton) },
        {
            key: "orderShipId",
            label: "关联船运单",
            width: 176,
            prop: "orderShipId",
            render: ({ row }) =>
                row.orderShipId ? (
                    <Link to={`/logistics/shippingNeed/waybillManagementInfo/${row.orderShipId}`}>
                        {row.orderShipId}
                    </Link>
                ) : (
                    DEFAULT_VALUE
                )
        },
        {
            key: "captinMobile",
            label: "手机号",
            width: 176,
            prop: "captinMobile",
            render: ({ row }) => display(row.captinMobile)
        },
        {
            key: "remark",
            label: "备注",
            minWidth: 350,
            prop: "remark",
            render: ({ row }) => display((row as DeliveryShip & { remark?: unknown }).remark)
        }
    ];

    /** Vue close 方法返回提货列表并携带刷新标记。 */
    const close = () => {
        void navigate("/purchasingManage/delivery?flushed=true");
    };

    return (
        <section aria-label="delivery-detail-page" className="detail-page-transparent flex min-h-full flex-col">
            <div className="section-card mb-8">
                <ZhDetailHeader
                    leftInfos={[{ label: "编号", value: display(purchaseGoods?.id) }]}
                    rightInfos={[
                        {
                            label: "创建时间",
                            value: purchaseGoods?.createdTime
                                ? dateFormat(purchaseGoods.createdTime, DATETIME_PICKER_FORMAT)
                                : DEFAULT_VALUE
                        }
                    ]}
                    stateDesc={stateLabel(purchaseGoods?.state)}
                    title="提货详情"
                />
            </div>

            {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}

            <div className="section-card mb-8">
                <div className="section-title">基本信息</div>
                <ZhGrid cols={4} gap={[20, 20]}>
                    <ZhBaseItem label="所属项目">
                        {purchaseGoods?.projectId != null && purchaseGoods.projectName ? (
                            <Link to={`/purchasingManage/project/info/${purchaseGoods.projectId}`}>
                                {purchaseGoods.projectName}
                            </Link>
                        ) : (
                            display(purchaseGoods?.projectName)
                        )}
                    </ZhBaseItem>
                    <ZhBaseItem
                        label="类型"
                        value={PAYMENT_TYPE.getIntroByCode(purchaseGoods?.type) || DEFAULT_VALUE}
                    />
                    <ZhBaseItem label="品类" value={display(purchaseGoods?.productTypeName)} />
                    <span aria-hidden="true" />
                    <ZhBaseItem label="收货单位" value={enterpriseLabel(purchaseGoods?.receiverEnterprise)} />
                    <ZhBaseItem label="供货单位" value={enterpriseLabel(purchaseGoods?.supplierEnterprise)} />
                    <ZhBaseItem
                        label="预估单价"
                        value={`${formatNumber(purchaseGoods?.estimatedUnitPrice, 2)} 元/吨`}
                    />
                    <ZhBaseItem label="吨数" value={formatNumber(purchaseGoods?.quantity)} />
                    <ZhBaseItem
                        label="提货申请日期"
                        value={
                            purchaseGoods?.applyDate
                                ? dateFormat(purchaseGoods.applyDate, DATE_PICKER_FORMAT)
                                : DEFAULT_VALUE
                        }
                    />
                    <ZhBaseItem
                        label="预计提货日期"
                        value={
                            purchaseGoods?.expectedDeliveryDate
                                ? dateFormat(purchaseGoods.expectedDeliveryDate, DATE_PICKER_FORMAT)
                                : DEFAULT_VALUE
                        }
                    />
                    <ZhBaseItem
                        label="签收状态"
                        value={SIGNATURE_STATUS.getIntroByCode(purchaseGoods?.receiptState) || DEFAULT_VALUE}
                    />
                    <ZhBaseItem label="关联的签收单">
                        {purchaseGoods?.goodsReceiptId ? (
                            <Link to={`/purchasingManage/receiptManagement/detail/${purchaseGoods.goodsReceiptId}`}>
                                {purchaseGoods.goodsReceiptId}
                            </Link>
                        ) : (
                            DEFAULT_VALUE
                        )}
                    </ZhBaseItem>
                    <ZhBaseItem label="凭证文件" span={4}>
                        {detail?.file?.id ? (
                            <span className="inline-flex items-center gap-4">
                                <span>{display(detail.file.name || detail.file.id)}</span>
                                {detail.file.path ? (
                                    <a href={detail.file.path} rel="noreferrer" target="_blank">
                                        查看/下载
                                    </a>
                                ) : null}
                            </span>
                        ) : (
                            DEFAULT_VALUE
                        )}
                    </ZhBaseItem>
                </ZhGrid>
            </div>

            {invalid ? (
                <div className="section-card mb-8">
                    <div className="section-title">作废信息</div>
                    <ZhGrid cols={4} gap={[20, 20]}>
                        <ZhBaseItem label="文件">
                            {invalidFile?.id ? (
                                <span className="inline-flex items-center gap-4">
                                    <span>{display(invalidFile.name || invalidFile.id)}</span>
                                    {invalidFile.path ? (
                                        <a href={invalidFile.path} rel="noreferrer" target="_blank">
                                            查看/下载
                                        </a>
                                    ) : null}
                                </span>
                            ) : (
                                display(purchaseGoods?.invalidFileId)
                            )}
                        </ZhBaseItem>
                        {fullyInvalid ? (
                            <ZhBaseItem
                                label="时间"
                                value={
                                    purchaseGoods?.invalidTime
                                        ? dateTimeFormat(purchaseGoods.invalidTime)
                                        : DEFAULT_VALUE
                                }
                            />
                        ) : null}
                    </ZhGrid>
                </div>
            ) : null}

            <div className="section-card mb-8">
                <div className="section-title">关联船运单</div>
                <ZhTable<DeliveryShip>
                    data={ships}
                    columns={shipColumns}
                    pagination={false}
                    rowKey={row => String(row.id || "ship")}
                />
            </div>

            <Button className="operation-fixed-btn" onClick={close}>
                返回
            </Button>
        </section>
    );
}

export default DeliveryDetailPage;
